// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import clsx from "clsx";
import { FC, useEffect, useId } from "react";

import { PlannerInsert } from "../section/planner/PlannerInsert";

import { SessionInProgressChip } from "./SessionInProgressChip";

import { serverGetHomePlannerPages, serverGetPageAssessmentStatuses } from "api/api-server-data";
import { SectionIcon } from "components/materials/section/SectionIcon";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookSectionIcon, ChevronDownIcon, ChevronUpIcon } from "mds/icons";
import { useHomeRole } from "providers/HomeRoleProvider";
import { slices, storeApi, useAppDispatch } from "store/index";
import {
  courseSelectors,
  sectionSelectors,
  selectHomeSectionIdsForTerm,
  selectHomeTermIdsForCurrentUser,
  selectSectionIdsForCurrentUser,
  termSelectors,
} from "store/selectors";
import { computedStateActions } from "store/slices/computed";
import { useHasItemsSelector, useListSelector, useObjectSelector } from "store/store-hooks";
import { getCourseAndSectionDisplayTitle } from "utils/course";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { sectionHomeUrl } from "utils/urls";

const HomeSectionListItem: FC<{ sectionId: string }> = ({ sectionId }) => {
  const { isArchived } = useIsArchived();
  const section = useObjectSelector(
    (state) => sectionSelectors.selectById(state, sectionId),
    ["id", "course_id", "title"],
  );
  // Courses may not be loaded on first paint; re-render when they arrive.
  const course = useObjectSelector(
    (state) => courseSelectors.selectById(state, section?.course_id),
    ["code", "title"],
  );

  if (!section || !course) {
    return null;
  }

  return (
    <ListItem
      className="w-full justify-between first:border-t-0"
      kind="primary"
      to={sectionHomeUrl(sectionId)}
    >
      <div className="flex min-w-0 flex-auto">
        <IconText
          className={clsx("truncate", {
            "text-black-tint-55": isArchived(section),
          })}
          iconStart={<SectionIcon sectionId={sectionId} />}
          text={
            <span className="min-w-12 truncate">
              {getCourseAndSectionDisplayTitle(course, section)}
            </span>
          }
        />
      </div>
      <SessionInProgressChip sectionId={sectionId} />
    </ListItem>
  );
};

type HomeTermSectionListProps = {
  termId: string;
  listId: string;
  archived?: boolean;
};

const HomeTermSectionList: FC<HomeTermSectionListProps> = ({
  termId,
  listId,
  archived = false,
}) => {
  const homeRole = useHomeRole();
  const term = useObjectSelector((state) => termSelectors.selectById(state, termId), ["title"]);
  const sectionIds = useListSelector((state) =>
    selectHomeSectionIdsForTerm(state, homeRole, termId),
  );

  if (!term) {
    return null;
  }

  return (
    <Disclosure defaultOpen>
      {({ open }) => {
        const titleId = `${listId}-${termId}`;
        return (
          <div className={clsx(archived && "ml-6")}>
            <DisclosureButton
              className={clsx(
                "flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium",
                archived ? "text-black-tint-55" : "text-black-tint-10",
              )}
              title={open ? t("tooltip.collapse") : t("tooltip.expand")}
            >
              <span id={titleId}>{term.title}</span>
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </DisclosureButton>

            <DisclosurePanel className="flex flex-col gap-2">
              <ol aria-labelledby={titleId} className="flex flex-col">
                {sectionIds.map((sectionId) => (
                  <HomeSectionListItem key={sectionId} sectionId={sectionId} />
                ))}
              </ol>
            </DisclosurePanel>
          </div>
        );
      }}
    </Disclosure>
  );
};

export const HomeSectionList: FC = () => {
  const homeRole = useHomeRole();
  const listId = useId();
  const dispatch = useAppDispatch();
  const hasSections = useHasItemsSelector((state) =>
    selectSectionIdsForCurrentUser(state, homeRole),
  );
  const activeTermIds = useListSelector((state) =>
    selectHomeTermIdsForCurrentUser(state, homeRole, false),
  );
  const archivedTermIds = useListSelector((state) =>
    selectHomeTermIdsForCurrentUser(state, homeRole, true),
  );
  const isSmallScreen = !useIsMdOrLarger();

  useEffect(() => {
    if (!homeRole) return;

    const loadPlannerData = async () => {
      const pages = await serverGetHomePlannerPages(homeRole);
      if (pages?.length) {
        dispatch(slices.pages.actions.updateMany(pages));

        const pageIds = pages.map((p) => p.id);

        storeApi.page_access_data.list({ page_id__in: pageIds });
        const statuses = await serverGetPageAssessmentStatuses({ pageIds });

        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
      }
    };

    loadPlannerData();
  }, [dispatch, homeRole]);

  if (!hasSections) {
    return (
      <EmptyCard
        className="mb-4"
        description={t("home.no_sections_for_role_description")}
        icon={<BookSectionIcon className="text-blue" />}
        title={t("home.no_sections_for_role_title")}
      />
    );
  }

  return (
    <div className="flex h-full flex-col-reverse gap-6 overflow-y-auto md:flex-row md:items-start md:gap-10">
      <div className="flex flex-1 flex-col gap-8">
        {activeTermIds.map((termId) => (
          <HomeTermSectionList key={termId} listId={listId} termId={termId} />
        ))}

        {archivedTermIds.length > 0 && (
          <Disclosure key="archived-terms" defaultOpen>
            {({ open }) => {
              const titleId = `${listId}-archived-terms`;
              return (
                <div>
                  <DisclosureButton
                    className="flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium text-black-tint-55"
                    title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                  >
                    <span id={titleId}>{t("home.terms.archived")}</span>
                    {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </DisclosureButton>

                  <DisclosurePanel className="flex flex-col gap-2">
                    {archivedTermIds.map((termId) => (
                      <HomeTermSectionList key={termId} listId={listId} termId={termId} archived />
                    ))}
                  </DisclosurePanel>
                </div>
              );
            }}
          </Disclosure>
        )}
      </div>

      <div className={clsx("flex-shrink-0", !isSmallScreen && "mb-6")}>
        <PlannerInsert startOpen={!isSmallScreen} />
      </div>
    </div>
  );
};
